/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  getExternalAppointmentConfig,
  getTimeAvailability,
} from 'api/AppointmentController';
import { getRepsAndOpenHousesForListing } from 'api/ListingController';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { addHours, addMilliseconds, addMinutes, isPast } from 'date-fns';
import { getTimezoneOffset } from 'date-fns-tz';
import styled from 'styled-components';
import { Text } from 'components/Typography/Text';
import { SecondaryText, Spin } from '@brokerbay/design-components';
import { Select, message, Title, theme } from 'components';
import { getTimezoneCode } from 'utils';
import { MaxDuration, Timezone } from '@bb/domain/dist/types';
import { useBreakpoint } from 'hooks';
import { OpenHouse } from '@bb/domain/dist/types/Listing';
import { AuthContext } from 'contexts';
import TourEditContext from '../TourEditContext';
import TourListingContext from '../TourListingContext';
import TimeSelectorItem from './TimeSelectorItem';
import SVGNoMoreShowings from './SVGNoMoreShowings';
import { formatToZonedTime, checkIsIOS } from '../utils';
import ModalMobileHeader from './ModalMobileHeader';
import ModalMobileFooter from './ModalMobileFooter';
import MobileFriendlyTooltip from '../MobileFriendlyTooltip';
import { useWindowHeight } from '../hooks';

const EARLY_TOMORROW = ['12:15 AM', '12:30 AM', '12:45 AM'];

const isTimeOpenHouse = (time: Date, openHouses: OpenHouse[]) => {
  let isOpenHouse = false;
  let isOpenHouseStart = false;
  let isOpenHouseEnd = false;

  for (let i = 0; i < openHouses.length; i++) {
    const openHouse = openHouses[i];
    if (new Date(openHouse.timeTo).getTime() === time.getTime()) {
      isOpenHouse = true;
      isOpenHouseEnd = true;
      break;
    }

    if (new Date(openHouse.timeFrom).getTime() === time.getTime()) {
      isOpenHouse = true;
      isOpenHouseStart = true;
      break;
    }

    if (
      new Date(openHouse.timeFrom).getTime() < time.getTime() &&
      time.getTime() < new Date(openHouse.timeTo).getTime()
    ) {
      isOpenHouse = true;
      break;
    }
  }

  return { isOpenHouse, isOpenHouseStart, isOpenHouseEnd };
};
const regularDurationMinutes = [15, 30, 60];
const getClosestDuration = (startTime?: Date, endTime?: Date) => {
  if (!startTime || !endTime) {
    return regularDurationMinutes[regularDurationMinutes.length - 1];
  }

  const duration = Math.round(
    (endTime.getTime() - startTime.getTime()) / (1000 * 60),
  );

  const closestDuration = regularDurationMinutes.reduce(
    (prev, curr) =>
      Math.abs(curr - duration) < Math.abs(prev - duration) ? curr : prev,
    15,
  );
  return closestDuration;
};

interface TimeSelectorDropdownOverlayProps {
  listingId: string;
  listingTimezone: Timezone;
  startTime?: Date;
  endTime?: Date;
  itemId: string;
  triggerRef?: React.MutableRefObject<any>;
  fromModal?: boolean;
  toggleDropdownVisibility?: () => void;
  setDropdownVisible?: React.Dispatch<React.SetStateAction<boolean>>;
}

const TimeSelectorDropdownOverlay: React.FC<
  TimeSelectorDropdownOverlayProps
> = ({
  listingId,
  listingTimezone,
  startTime,
  endTime,
  itemId,
  fromModal,
  toggleDropdownVisibility,
  setDropdownVisible,
  triggerRef,
}) => {
  const { tour, setTourItemTime, modalFooterRef } = useContext(TourEditContext);
  const { auth } = useContext(AuthContext);
  const { mobileFooterRef, setSelectedTimes } = useContext(TourListingContext);
  const [availableTimes, setAvailableTimes] = useState<Record<string, boolean>>(
    {},
  );
  const [isLoading, setIsLoading] = useState(true);
  const [showingTimesHeight, setShowingTimesHeight] = useState(0);

  const [selectedDuration, setSelectedDuration] = useState(15); // in minutes
  const [externalConfig, setExternalConfig] = useState<{
    noMoreShowings?: boolean;
    maxDuration: MaxDuration;
    isVirtualTourEnabled?: boolean;
  }>();
  const [openHouses, setOpenHouses] = useState<OpenHouse[]>([]);
  const { lg } = useBreakpoint();
  const isIOS = !!checkIsIOS();
  const windowHeight = useWindowHeight();

  const containerRef = useRef(null);
  const timezoneRef = useRef(null);
  const durationsRef = useRef(null);

  const timezoneOffset = timezoneRef?.current?.getBoundingClientRect().bottom;
  const durationsOffset = durationsRef?.current?.getBoundingClientRect().bottom;

  const handleMouseClicks = useCallback(({ target }: MouseEvent) => {
    if (
      setDropdownVisible &&
      !containerRef.current.contains(target) &&
      target !== triggerRef?.current
    ) {
      setDropdownVisible(false);
    }
  }, []);

  const isTimeValid = useCallback(
    (
      startTime_: Date,
      duration: number,
      listingTimezone_: Timezone,
    ): {
      isValid: boolean;
      passedTourDate?: boolean;
    } => {
      const showingEndTime = addMinutes(startTime_, duration);
      let currentTime = new Date(startTime_);

      const passedTourDate = EARLY_TOMORROW.includes(
        formatToZonedTime(showingEndTime, 'h:mm a', listingTimezone_),
      );

      while (currentTime.getTime() < showingEndTime.getTime()) {
        const timestamp =
          availableTimes[
            formatToZonedTime(currentTime, 'h:mm a', listingTimezone_)
          ];
        if (!timestamp) {
          return {
            isValid: false,
            passedTourDate,
          };
        }

        currentTime = addMinutes(currentTime, 15);
      }
      return { isValid: true };
    },
    [availableTimes],
  );

  const scrollToAvailableTime = useCallback(node => {
    if (node !== null) {
      node.scrollIntoView();
    }
  }, []);

  const handleDurationChange = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    const selectedDurationTemp = parseInt(
      e.currentTarget.getAttribute('data-duration'),
      10,
    );

    if (startTime) {
      const { isValid, passedTourDate } = isTimeValid(
        startTime,
        selectedDurationTemp,
        listingTimezone,
      );
      if (isValid) {
        const newEndTime = addMinutes(startTime, selectedDurationTemp);
        setSelectedDuration(selectedDurationTemp);
        setTourItemTime(startTime, newEndTime, itemId);
        if (fromModal) {
          setSelectedTimes([startTime, newEndTime]);
        }
      }
      if (passedTourDate) {
        message.warn('Time cannot be selected past tour date.');
      }
    } else {
      setSelectedDuration(selectedDurationTemp);
    }
  };

  const { timesOfDay, firstAvailableTime } = useMemo(() => {
    // populating timesOfDay based on the item's timezone and finding the firstAvailableTime between timesOfDay
    if (tour?.date && listingTimezone && availableTimes) {
      let firstAvailableTimeTemp;
      const userTimezone =
        auth.org.addresses[parseInt(auth.location, 10)][
          parseInt(auth.address, 10)
        ].timezone;
      const userTimezoneOffset = getTimezoneOffset(userTimezone, tour.date);
      const listingTimezoneOffset = getTimezoneOffset(
        listingTimezone,
        tour.date,
      );
      const dayStartListingTimezone = addMilliseconds(
        tour.date,
        userTimezoneOffset - listingTimezoneOffset,
      );
      const dayEndListingTimezone = addHours(dayStartListingTimezone, 24);
      let timeOfDay = new Date(dayStartListingTimezone.getTime());
      const timesOfDayTemp: Date[] = [];
      while (timeOfDay.getTime() < dayEndListingTimezone.getTime()) {
        timesOfDayTemp.push(timeOfDay);
        if (
          !firstAvailableTimeTemp &&
          !isPast(timeOfDay) &&
          availableTimes[
            formatToZonedTime(timeOfDay, 'h:mm a', listingTimezone)
          ]
        ) {
          firstAvailableTimeTemp = timeOfDay;
        }
        timeOfDay = addMinutes(timeOfDay, 15);
      }
      return {
        timesOfDay: timesOfDayTemp,
        firstAvailableTime: firstAvailableTimeTemp,
      };
    }
  }, [tour?.date, availableTimes]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userTimezone =
          auth.org.addresses[parseInt(auth.location, 10)][
            parseInt(auth.address, 10)
          ].timezone;

        const [listingData, availableTimesData, externalConfigData] =
          await Promise.all([
            getRepsAndOpenHousesForListing(listingId),
            getTimeAvailability({
              listingId,
              dateStr: formatToZonedTime(tour.date, 'yyyy-MM-dd', userTimezone),
              checkUnavailableDays: true,
            }),
            getExternalAppointmentConfig(listingId),
          ]);

        setExternalConfig(externalConfigData);
        setSelectedDuration(
          Math.min(
            getClosestDuration(startTime, endTime),
            externalConfigData.maxDuration,
          ),
        );
        setAvailableTimes(availableTimesData);
        setOpenHouses(listingData.openHouses);
      } catch (err) {
        message.error('Error fetching the available showing times');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [listingTimezone, tour.date, auth?.id]);

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseClicks);

    return () => {
      document.removeEventListener('mouseup', handleMouseClicks);
    };
  }, [handleMouseClicks]);

  useEffect(() => {
    const modalFooterNode = modalFooterRef?.current;

    const tourListingFooterNode = mobileFooterRef?.current
      ?.parentNode as HTMLElement;

    const footerNode = tourListingFooterNode || modalFooterNode || null;
    const headerOffset = externalConfig?.noMoreShowings
      ? durationsOffset
      : timezoneOffset;

    if (!headerOffset || !footerNode) return;

    const containerHeight =
      windowHeight - headerOffset - footerNode.clientHeight;

    setShowingTimesHeight(containerHeight);
  }, [timezoneOffset, durationsOffset, mobileFooterRef?.current?.parentNode]);

  return (
    <DropdownContainer
      ref={containerRef}
      $lg={lg}
      $fromModal={fromModal}
      $isIOS={isIOS}
    >
      {!lg && !fromModal && (
        <ModalMobileHeader
          title="Pick Time"
          handleBackIconClick={toggleDropdownVisibility}
        />
      )}
      <DurationsContainer $lg={lg} ref={durationsRef}>
        {regularDurationMinutes.map(min =>
          min <= externalConfig?.maxDuration ? (
            <DurationItem
              key={min}
              data-duration={min}
              $selected={selectedDuration === min}
              onClick={handleDurationChange}
            >
              {`${min}m`}
            </DurationItem>
          ) : (
            <MobileFriendlyTooltip
              key={min}
              title="This duration is unavailable"
              // @ts-ignore antd didn't type this properly
              zIndex={4000 + 1} // listing modal has 4000, so we need to add 1
            >
              <DurationItem data-duration={min} $unavailable>
                {`${min}m`}
              </DurationItem>
            </MobileFriendlyTooltip>
          ),
        )}
      </DurationsContainer>
      {!externalConfig?.noMoreShowings && (
        <div ref={timezoneRef}>
          <TimezoneText color={theme.gray8} textAlign="center" $lg={lg}>
            Times reflect the listing’s timezone (
            {getTimezoneCode(listingTimezone)})
          </TimezoneText>
        </div>
      )}
      <ShowingTimesContainer $lg={lg} $height={showingTimesHeight}>
        {isLoading && (
          <StyledSpinnerContainer>
            <Spin />
          </StyledSpinnerContainer>
        )}
        {!isLoading && (
          <>
            {externalConfig.noMoreShowings && (
              <StyledNoMoreShowings $lg={lg} $height={showingTimesHeight}>
                <SVGNoMoreShowings />
                <StyledTitle level={3} textAlign="center">
                  Showings are turned off for this listing
                </StyledTitle>
                <StyledSecondaryText textAlign="center">
                  For more information, please call the listing’s brokerage
                </StyledSecondaryText>
              </StyledNoMoreShowings>
            )}
            {!externalConfig.noMoreShowings && (
              <>
                {timesOfDay?.map(time => (
                    <TimeSelectorItem
                      ref={
                        time.getTime() === firstAvailableTime?.getTime()
                          ? scrollToAvailableTime
                          : undefined
                      }
                      key={time.toISOString()}
                      time={time}
                      listingTimezone={listingTimezone}
                      selectedDuration={selectedDuration}
                      isStartTime={time.getTime() === startTime?.getTime()}
                      timePassed={isPast(time)}
                      isSelected={
                        time.getTime() >= startTime?.getTime() &&
                        time.getTime() < endTime?.getTime()
                      }
                      itemId={itemId}
                      isTimeValid={isTimeValid}
                      isTimeOpenHouse={isTimeOpenHouse(time, openHouses)}
                      toggleDropdownVisibility={toggleDropdownVisibility}
                      unavailable={
                        !availableTimes[
                          formatToZonedTime(time, 'h:mm a', listingTimezone)
                        ]
                      }
                      fromModal={fromModal}
                    />
                  ))}
              </>
            )}
          </>
        )}
      </ShowingTimesContainer>
      {!lg && !fromModal && (
        <ModalMobileFooter
          buttonText="Done"
          onButtonClick={toggleDropdownVisibility}
        />
      )}
    </DropdownContainer>
  );
};

export default TimeSelectorDropdownOverlay;

const DropdownContainer = styled.div<{
  $lg: boolean;
  $fromModal?: boolean;
  $isIOS: boolean;
}>`
  display: flex;
  flex-direction: column;
  background-color: white;
  ${({ $lg }) =>
    $lg &&
    'box-shadow: 0 3px 6px -4px #0000001f, 0 6px 16px #00000014, 0 9px 28px 8px #0000000d;'};
  ${({ $lg, $fromModal }) => !$lg && !$fromModal && 'height:100vh;'};
  ${({ $lg, $fromModal, $isIOS }) =>
    !$lg && !$fromModal && $isIOS && 'height: -webkit-fill-available;'};
  width: ${({ $fromModal, $lg }) =>
    $fromModal && $lg ? '100%' : $lg ? '243px' : '100vw'};
`;

const ShowingTimesContainer = styled.div<{
  $lg: boolean;
  $height: number;
}>`
  max-height: calc((100vh / 2 - 40px));
  ${({ $lg }) => !$lg && `max-height: 100vh;`}
  ${({ $lg }) => $lg && 'height: 400px'};
  ${({ $lg, $height }) => !$lg && $height && `height: ${$height}px`};
  overflow-y: scroll;
`;

const DurationsContainer = styled.div<{ $lg: boolean }>`
  display: flex;
  flex-direction: row;
  background-color: white;
  ${({ $lg }) => !$lg && 'padding: 1.4rem;'};
`;

const DurationItem = styled.div<{
  $selected?: boolean;
  $unavailable?: boolean;
}>`
  cursor: pointer;
  text-align: center;
  flex-grow: 1;
  font-size: 14px;
  padding: 5px 0px;
  box-shadow:
    inset 1px 0px 0px #d9d9d9,
    inset -1px 0px 0px #d9d9d9,
    inset 0px 1px 0px #d9d9d9,
    inset 0px -1px 0px #d9d9d9;

  ${({ $selected }) => $selected && `color: #1665FC`}
  ${({ $unavailable }) =>
    $unavailable &&
    `color: rgba(0, 0, 0, 0.25); background-color: #F5F5F5; cursor: default;`}
`;

const TimezoneText = styled(Text)<{ $lg: boolean }>`
  box-shadow: inset 0px -1px 0px #f0f0f0;
  padding: 1rem 0;
  font-size: 1.2rem;
  background-color: white;
  ${({ $lg }) => !$lg && 'border-top: 1px solid #f0f0f0;'};
`;

const StyledNoMoreShowings = styled.div<{
  $lg: boolean;
  $height: number;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  ${({ $lg }) => $lg && 'height: 400px'};
  ${({ $lg, $height }) => !$lg && $height && `height: ${$height}px`};
`;

const StyledTitle = styled(Title)`
  margin: 1.6rem 0 !important;
  width: 18rem;
`;

const StyledSecondaryText = styled(SecondaryText)`
  font-size: 12px;
  width: 14.4rem !important;
`;

const StyledSpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;
